import queryString from 'query-string'
import React, { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectAllUsersFromState,
  selectUsersFullPaginatedData,
} from 'src/app/Budgets/redux/budgets.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import BudgetsFilters, {
  BudgetsFilterValues,
  hasBudgetsFilters,
} from 'src/feature/BudgetsFilters/BudgetsFilters'
import CardBudgetsTable from 'src/feature/CardBudgetsTable/CardBudgetsTable'
import { useBackgroundDispatch, useWindowSize } from 'src/hooks'
import PluralTrns from 'src/localization/PluralTrns'
import { CreditCardStatus, CreditCardType } from 'src/models/creditCard.models'
import { budgetOverviewUrl } from 'src/navigation/urls'
import { useQueryCreditCards } from 'src/queries/creditCard.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { getUsers } from 'src/redux/actions/data/users.actions'
import {
  selectBackgroundQueryParams,
  selectMyWalletBudgetsMatch,
} from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

/** Matches the legacy page's size, so paging behaviour is unchanged. */
const BUDGETS_PER_PAGE = 10

/** The user dropdowns load their options ten at a time, as legacy did. */
const USERS_PER_PAGE = 10

/**
 * The page chrome above the card — LayoutShell title and the family tab bar.
 * Same constant the legacy `BudgetsContent` used for the identical purpose.
 */
const PAGE_CHROME_HEIGHT = 122

/**
 * The card's OWN chrome, above and below the grid: `ContentCard` padding
 * (`spacing(6)` top and bottom, 48px), the heading row the Filters button sizes
 * to (~40px), and that row's `spacing(3)` margin (12px).
 *
 * Legacy never needed this — its filter bar was a separate box ABOVE the
 * measured region, so `viewport - 122` was the whole answer. Now the heading
 * lives inside the card, so a grid of `viewport - 122` pushes the card that
 * much past the fold.
 */
const CARD_CHROME_HEIGHT = 100

/** Never collapse the grid to nothing on a short viewport. */
const MIN_GRID_HEIGHT = 240

/**
 * Reads one repeated query param as a list of real selections.
 *
 * `query-string` gives back a bare string for `?users=a` and an array for
 * `?users=a&users=b`, so every read has to normalize. It also gives back `''`
 * for `?users=` and `null` for a valueless `?users` — neither is a selection,
 * and letting either through is not merely untidy: `hasBudgetsFilters` counts a
 * non-empty array, so `?users=` alone would put the whole page into its
 * filtered state — "No Budgets match your search" over an unfiltered list, the
 * filtered count label, a Filters pip reading 1 — and send `users: ['']` to the
 * API.
 *
 * Trims and drops empties for the same reason, and in the same shape, as
 * `getStringQueryParam` in `src/navigation/queryParams.ts`.
 */
const paramList = (
  param: string | (string | null)[] | null | undefined,
): string[] => {
  if (param === null || param === undefined) return []
  const values = Array.isArray(param) ? param : [param]
  return values
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => value.length > 0)
}

/**
 * The statuses to ASK the API for, which is not quite what the checkboxes say.
 *
 * An expired budget reads as inactive to a user but is its own status on the
 * wire, so "Inactive" has always meant Inactive-or-Expired here. Widened at the
 * request boundary rather than in the checkbox state, so the URL keeps one
 * token per visible choice and a shared link reproduces the same two boxes.
 */
/** The statuses the wire actually defines, for validating what the URL claims. */
const KNOWN_STATUSES = new Set<string>(Object.values(CreditCardStatus))

const requestStatuses = (statuses: CreditCardStatus[]): CreditCardStatus[] =>
  statuses.includes(CreditCardStatus.Inactive)
    ? [...statuses, CreditCardStatus.Expired]
    : statuses

/**
 * The redesigned org-wide budgets list (EX-40737).
 *
 * Replaces `BudgetsAllBudgets` → `BudgetsContent` → `BudgetsBoxWithFilters`
 * when `isUpdatedAllBudgetsTableEnabled` is on, and serves both surfaces that
 * legacy chain served: `/budgets` (All Budgets) and My Wallet → Your Budgets,
 * which differ only by a `user: 'me'` on the request.
 *
 * Three things change together, which is why this is a new page rather than an
 * edit to the old one:
 *
 *  - the TABLE is `CardBudgetsTable`, the same component the card-detail tabs
 *    render, so a budget reports the same columns wherever it is listed. Its
 *    middle column is Budget User here, NOT Cards: this list pages without
 *    bound, and the Cards column costs a statistics request carrying one COUNT
 *    per budget loaded so far. Budget User rides along on the rows themselves.
 *  - the DATA comes from `useQueryCreditCards` instead of the Redux
 *    `getCreditCards` thunk and the `app.budgets` paginated slice. That thunk
 *    also fan-outs FOUR per-page statistics batches on every fetch (active and
 *    total budget counts, active and total VCN counts) for numbers this table
 *    does not show; none of them survive the move.
 *  - the FILTER state lives in the URL, full stop. Legacy kept it in three
 *    places at once — component state, a debounced `window.history.pushState`,
 *    and an effect that read the params back out — and the initial-render guard
 *    that dance needed is gone with it.
 *
 * Routing stays here: `BudgetsFilters` and `CardBudgetsTable` are both
 * routing-free and take their values as props.
 */
export default function BudgetsAllBudgetsTab(): ReactElement {
  const history = useHistory()
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()
  const appName = useSelector(selectAppName)
  // Resize-aware, like the legacy page: the grid is sized in pixels, so a
  // window resize has to re-measure or the table keeps its old height.
  const [, windowHeight] = useWindowSize()

  /*
    Your Budgets vs All Budgets. The only difference between the two is the
    `user: 'me'` below and which total the heading counts against — they share
    this page, as they shared `BudgetsContent`.
  */
  const isYourBudgets = useSelector(selectMyWalletBudgetsMatch) !== null

  /*
    The URL is the filter state. Not a copy of it, not seeded from it — the
    single source, read straight back out on every render.

    That is what makes a filtered list shareable and back-navigable for free,
    and it removes the three-way sync legacy needed to approximate the same
    thing. `selectQueryParams` is memoized on `location`, so this recomputes
    only when the URL actually changes.
  */
  /*
    The BACKGROUND location's params, not the live URL's.

    This page is rendered under `<Switch location={calculatedBackgroundLocation}>`
    and identifies itself with `selectMyWalletBudgetsMatch`, which is also a
    background match — so reading `selectQueryParams` (the live
    `state.router.location`) meant the two disagreed the moment a modal route
    opened over the list. Create New -> Budget, the empty state's own Create a
    Budget link and notification deep links all leave this component mounted
    while `location.search` belongs to the modal, which emptied `filterValues`,
    changed the query key, and refetched the list UNFILTERED behind the modal —
    then again on dismiss. `creditCardQueries.list` sets `gcTime: 0`, so neither
    result is cached and that is two real round trips per modal open/close, with
    the heading count flipping to the unfiltered statistic underneath.

    With no modal open the two selectors resolve to the same location, so this
    changes nothing in the ordinary case.
  */
  const queryParams = useSelector(selectBackgroundQueryParams)
  const filterValues: BudgetsFilterValues = useMemo(
    () => ({
      /*
        Validated, not cast. The URL is user input: `?statuses=active` from a
        hand-edited or stale link would otherwise reach the API as a status it
        rejects — landing the reader on an error body instead of a list — and
        `requestStatuses` would not widen it to include EXPIRED either, so a
        near-miss silently changes what is queried.
      */
      statuses: paramList(queryParams.statuses).filter(
        (value): value is CreditCardStatus => KNOWN_STATUSES.has(value),
      ),
      users: paramList(queryParams.users),
      owners: paramList(queryParams.owners),
    }),
    [queryParams.statuses, queryParams.users, queryParams.owners],
  )

  const isFiltered = hasBudgetsFilters(filterValues)

  /*
    `replace`, not `push`.

    Legacy pushed a history entry per (debounced) filter change, so backing out
    of a list the user had narrowed three times meant three Back presses through
    states they never chose to visit. A filter change is a refinement of the
    current view, not a navigation away from it.
  */
  const handleFiltersChange = useCallback(
    (next: BudgetsFilterValues): void => {
      const search = queryString.stringify({
        statuses: next.statuses,
        users: next.users,
        owners: next.owners,
      })
      history.replace({ search: search.length > 0 ? `?${search}` : '' })

      /*
        Only when something is actually selected. Clearing the last filter is
        still a change, and reporting it as "filter applied" — with every
        property false — would count filter-REMOVAL as filter-use and roughly
        double the event for anyone who toggles a filter off. Legacy guarded the
        same way (`Object.values(selectedFilters).some(arr => arr.length > 0)`).
      */
      if (!hasBudgetsFilters(next)) return

      analytics?.track(
        `${isYourBudgets ? 'your' : 'all'} budgets filter applied`,
        {
          status: next.statuses.length > 0,
          budget_user: next.users.length > 0,
          budget_creator: next.owners.length > 0,
        },
      )
    },
    [history, analytics, isYourBudgets],
  )

  const filters = useMemo(
    () => ({
      type: CreditCardType.Delegate,
      count: BUDGETS_PER_PAGE,
      page: 0,
      sortField: 'activeUpdatedAt',
      ...(isYourBudgets && { user: 'me' }),
      ...(filterValues.statuses.length > 0 && {
        statuses: requestStatuses(filterValues.statuses),
      }),
      ...(filterValues.users.length > 0 && { users: filterValues.users }),
      ...(filterValues.owners.length > 0 && { owners: filterValues.owners }),
    }),
    [isYourBudgets, filterValues],
  )

  const {
    data,
    isPending,
    isError,
    isFetching,
    isFetchingNextPage,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
    complete,
  } = useQueryCreditCards({ filters })

  const budgets = useMemo(() => data?.creditCards ?? [], [data?.creditCards])

  /*
    The unfiltered total, for the "N Results (of M Budgets)" summary. Two stats
    rather than one because Your Budgets counts only this user's budgets, and
    All Budgets counts the org's ACTIVE ones — the same pair, and the same
    asymmetry, the legacy heading used.
  */
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsActiveDelegateCount,
      StatisticsKeys.CreditCardsDelegateUserMeCount,
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount,
    ] as const,
  })
  /*
    Left possibly-undefined rather than defaulted to 0: until the statistic
    lands there is no count to report, and "0 active budgets" over a list that
    is about to render rows is worse than no count at all.

    BOTH keys count ACTIVE delegates — `CreditCardsDelegateUserMeCount` carries
    `status: Active` despite its name (see the note on its definition), which is
    what makes "active" honest on both tabs.
  */
  const totalBudgets = isYourBudgets
    ? statisticsResults[StatisticsKeys.CreditCardsDelegateUserMeCount].data
    : statisticsResults[StatisticsKeys.CreditCardsActiveDelegateCount].data

  /*
    Whether this user could create a budget at all. Only reaches the empty
    state, which drops its Create Budget CTA for a user who cannot — the page's
    own header buttons own that decision for the non-empty case.

    `?? 0` cannot tell "the statistic has not landed" from "this user cannot
    create a budget", and on an org with no budgets the list request winning the
    race is the ordinary case: the illustrated empty state would paint WITHOUT
    its Create a Budget CTA, then grow one a beat later. That CTA is the whole
    point of restoring this empty state, so the table keeps showing its loading
    copy until both settle — see `isPending` below.
  */
  const canIssueBudgetsResult =
    statisticsResults[
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
    ]
  const readOnly = (canIssueBudgetsResult.data ?? 0) === 0

  /*
    Options for both user dropdowns, still on the Redux `getUsers` thunk.

    Deliberately NOT migrated with the budgets themselves: this is the dropdown's
    data, not the table's, it is shared with several other filter bars through
    the same slice, and moving it is its own slice of EX-39746.
  */
  const userOptions = useSelector(selectAllUsersFromState)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)

  const fetchUsers = useCallback(
    (filterInput?: string, page?: number): void => {
      dispatch(getUsers({ q: filterInput, count: USERS_PER_PAGE, page }))
    },
    [dispatch],
  )

  /*
    The dropdown's first page of options, once.

    Safe to key on `fetchUsers`: it closes over `useBackgroundDispatch`, which
    memoizes its own identity (unlike some of its legacy siblings — see the
    exhaustive-deps note in src/CLAUDE.md), so this is a mount-only fetch rather
    than one per render. Further pages are the dropdown's own business.
  */
  useEffect((): void => {
    fetchUsers()
  }, [fetchUsers])

  // `fetch` is called by the grid when it reaches the end of the loaded rows.
  // The page argument is ignored: the infinite query owns its own cursor, and
  // taking one from the caller is how the two get out of step.
  const fetchMore = useCallback((): void => {
    if (!hasNextPage || isFetching) return
    void fetchNextPage()
  }, [hasNextPage, isFetching, fetchNextPage])

  const handleSelectBudget = useCallback(
    (budgetId: string): void => {
      history.push(budgetOverviewUrl(budgetId))
    },
    [history],
  )

  /*
    The count opposite the heading.

    FILTERED, it reports the filtered total and nothing else. It used to read
    "34 Results (of 27 Budgets)", which could state a plain impossibility: the
    27 was an ACTIVE-delegate statistic, while filtering to Inactive returns
    budgets that statistic never counted — so the "part" ran past the "whole".
    The two numbers were never measuring the same population, and no phrasing
    of a comparison between them would have been true.

    It also reads `totalItems` off the response rather than counting the rows in
    hand. `budgets.length` is what has been PAGED IN so far, so the heading
    rewrote itself 10 → 20 → 30 as the user scrolled one unchanged result set.

    UNFILTERED, it stays the active-delegate statistic, which is the figure this
    page has always led with.
  */
  /*
    `isPlaceholderData` is the precise signal that `data` still belongs to the
    PREVIOUS filter set.

    `isFiltered` comes from the URL and flips the instant the user checks a box,
    while `keepPreviousData` holds the old rows until the new request lands — so
    without this the heading briefly labels the previous query's total as the
    new filtered count ("38 budgets" for a filter that will return 4). Saying
    nothing for that moment beats saying a wrong number.
  */
  const filteredTotal = isPlaceholderData ? undefined : data?.totalItems

  const countLabel = isFiltered ? (
    filteredTotal === undefined ? undefined : (
      <PluralTrns
        pluralSource={'${count} budgets'}
        singleSource={'1 budget'}
        count={filteredTotal}
      />
    )
  ) : totalBudgets === undefined ? undefined : (
    <PluralTrns
      pluralSource={'${count} active budgets'}
      singleSource={'1 active budget'}
      count={totalBudgets}
    />
  )

  return (
    <>
      <Helmet>
        <title>{`${isYourBudgets ? 'Your' : 'All'} Budgets | ${appName}`}</title>
        <meta
          name="description"
          content={`${isYourBudgets ? 'Your' : 'All'} Budgets page of ${appName}`}
        />
      </Helmet>
      {/*
        `noPadding`, like every other full-page table — the prop's own comment in
        BackgroundPageContent says as much. Without it the page gutters inset the
        card on all four sides, which made this table visibly narrower and
        shorter than the lists it sits alongside.
      */}
      <BackgroundPageContent noStackingIndex noPadding>
        <div data-testid="budgetsAllBudgetsTab-content">
          <CardBudgetsTable
            budgets={budgets}
            middleColumn="budgetUser"
            isPending={isPending || canIssueBudgetsResult.isPending}
            isError={isError}
            countLabel={countLabel}
            // In the card's OWN heading row, not a bar above it — one surface,
            // one heading, one type scale.
            headerActions={
              <BudgetsFilters
                values={filterValues}
                onChange={handleFiltersChange}
                /*
                  A FILTERED fetch, which is what the flyout's `loading` means.
                  `isFetching` alone is also true while paging, so scrolling to
                  page two dimmed the filter controls for a fetch that has
                  nothing to do with them.
                */
                loading={isFetching && !isFetchingNextPage}
                userOptions={userOptions}
                userInfiniteScrollProps={{
                  ...usersFullPaginatedData,
                  fetch: fetchUsers,
                }}
              />
            }
            // Rows are real links; the page owns the URL, the table owns the
            // click.
            budgetHref={budgetOverviewUrl}
            onSelectBudget={handleSelectBudget}
            emptyState={
              <TableEmptyState
                design={
                  isFiltered
                    ? TableEmptyStateDesigns.AccountBudgetsFiltered
                    : TableEmptyStateDesigns.AccountBudgets
                }
                readOnly={readOnly}
              />
            }
            infiniteScrollProps={{
              fetch: fetchMore,
              loading: isFetching,
              complete,
              hasNextPage,
            }}
            height={Math.max(
              windowHeight - PAGE_CHROME_HEIGHT - CARD_CHROME_HEIGHT,
              MIN_GRID_HEIGHT,
            )}
          />
        </div>
      </BackgroundPageContent>
    </>
  )
}
